import { useState } from 'react';

import { Button } from '../../../../../components/ui/Button';
import { Checkbox } from '../../../../../components/ui/Checkbox';
import { Dialog } from '../../../../../components/ui/Dialog';
import { Link } from '../../../../../components/ui/Link';
import { FieldHelp } from '../../../../../components/ui/FieldHelp';
import { InputNumber } from '../../../../../components/ui/InputNumber';
import { Select } from '../../../../../components/ui/Select';
import { Textarea } from '../../../../../components/ui/Textarea';
import { campusOptions, type PreviewOrder } from './order-preview-fixtures';
import styles from './BulkOrderDialog.module.css';
import { orderImportFeatures } from './order-import-features';

const ownerOptions = ['王思远', '李文静', '陈可欣', '周敏', '赵一鸣'].map((value) => ({ label: value, value }));
const roleOptions = ['课程顾问', '主销售', '副销售', '班主任'].map((value) => ({ label: value, value }));
const campusSelectOptions = campusOptions.map((value) => ({ label: value, value }));
const initialChecks = { owner: false, role: false, commission: false, performanceCampus: false, note: false };

interface BulkOrderDialogProps {
  orders: readonly PreviewOrder[];
  onApply: (patch: Partial<PreviewOrder>) => void;
  onClose: () => void;
}

export function BulkOrderDialog({ orders, onApply, onClose }: BulkOrderDialogProps) {
  const [checks, setChecks] = useState(initialChecks);
  const [values, setValues] = useState({ owner: '', role: '', commission: '', performanceCampus: '', note: '' });
  const [source, setSource] = useState('');
  const first = orders[0];
  const hasCheckedField = Object.values(checks).some(Boolean);
  function applyFirst() {
    if (!first) return;
    setValues({ owner: first.owner, role: first.role, commission: first.commission, performanceCampus: first.performanceCampus, note: first.note });
    setChecks({ owner: true, role: true, commission: true, performanceCampus: true, note: true });
    setSource(`已填入首条订单「${first.name || '未填写姓名'}」的数据，可调整字段后再应用。`);
  }
  function apply() {
    if (!hasCheckedField) return;
    onApply(Object.fromEntries(Object.entries(values).filter(([key]) => checks[key as keyof typeof checks])));
  }
  return <Dialog
    className={styles.dialog}
    description="仅更新勾选的字段。勾选字段后留空，可清空其原值。内部备注家长不可见。"
    modalLayer="nested"
    onOpenChange={(open) => { if (!open) onClose(); }}
    open
    title="批量设置订单级字段"
    width={600}
    footer={<div className={styles.footer}>
      <span className={styles.selectedCount}>已选{orders.length.toLocaleString('zh-CN')}条</span>
      {orderImportFeatures.bulkApplyFirst ? <>
        <Link disabled={!first} onClick={applyFirst} type="primary">应用首条数据</Link>
        <FieldHelp content="首条数据取自已选订单中的原表首条，填入后点击“应用到所选订单”生效。" label="应用首条数据说明" />
      </> : null}
      <span className={styles.footerSpace} />
      <Button onClick={onClose} type="default">取消</Button>
      <Button disabled={!hasCheckedField} onClick={apply} type="primary">应用到所选订单</Button>
    </div>}
  >
    <div className={styles.body}>
      <div aria-live="polite" className={styles.feedback}>{source ? <p className={styles.summary}>{source}</p> : null}</div>
      <div className={styles.field}>
        <Checkbox checked={checks.owner} label="业绩归属人" onCheckedChange={(checked) => setChecks((current) => ({ ...current, owner: checked }))} />
        <Select allowClear ariaLabel="批量设置业绩归属人" className={styles.control} disabled={!checks.owner} onValueChange={(value) => setValues((current) => ({ ...current, owner: value }))} options={ownerOptions} placeholder="留空可清空原值" value={values.owner} />
      </div>
      <div className={styles.field}>
        <Checkbox checked={checks.role} label="销售角色" onCheckedChange={(checked) => setChecks((current) => ({ ...current, role: checked }))} />
        <Select allowClear ariaLabel="批量设置销售角色" className={styles.control} disabled={!checks.role} onValueChange={(value) => setValues((current) => ({ ...current, role: value }))} options={roleOptions} placeholder="留空可清空原值" value={values.role} />
      </div>
      <div className={styles.field}>
        <Checkbox checked={checks.commission} label="提成比例（%）" onCheckedChange={(checked) => setChecks((current) => ({ ...current, commission: checked }))} />
        <InputNumber ariaLabel="批量设置提成比例" className={styles.numberControl} disabled={!checks.commission} max={100} min={0} onValueChange={(value) => setValues((current) => ({ ...current, commission: value === null ? '' : String(value) }))} placement="right" value={values.commission === '' ? null : Number(values.commission)} />
      </div>
      <div className={styles.field}>
        <Checkbox checked={checks.performanceCampus} label="业绩归属校区" onCheckedChange={(checked) => setChecks((current) => ({ ...current, performanceCampus: checked }))} />
        <Select allowClear ariaLabel="批量设置业绩归属校区" className={styles.control} disabled={!checks.performanceCampus} onValueChange={(value) => setValues((current) => ({ ...current, performanceCampus: value }))} options={campusSelectOptions} placeholder="留空可清空原值" value={values.performanceCampus} />
      </div>
      <div className={styles.field}>
        <Checkbox checked={checks.note} label="内部备注" onCheckedChange={(checked) => setChecks((current) => ({ ...current, note: checked }))} />
        <Textarea ariaLabel="批量设置内部备注" className={styles.control} disabled={!checks.note} onValueChange={(value) => setValues((current) => ({ ...current, note: value }))} placeholder="家长不可见" value={values.note} />
      </div>
    </div>
  </Dialog>;
}
